import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryClient';
import {
  listDiagrams,
  getDiagram,
  createDiagram,
  updateDiagram,
  patchDiagram,
  deleteDiagram,
  duplicateDiagram,
  moveDiagramToFolder,
  toggleDiagramSharing,
  regenerateShareToken,
  toggleDiagramStar,
  updateDiagramIcon,
  type CreateDiagramData,
  type UpdateDiagramData,
} from '../../services/diagramService';
import type { DiagramMetadata } from '../../types';

/**
 * Query hook for fetching all diagrams
 */
export function useDiagramsQuery(enabled = true) {
  return useQuery({
    queryKey: queryKeys.diagrams.list(),
    queryFn: () => listDiagrams(undefined, true), // Always bypass browser cache
    enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Query hook for fetching a single diagram
 */
export function useDiagramQuery(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.diagrams.detail(id!),
    queryFn: () => getDiagram(id!, true), // Always bypass browser cache
    enabled: enabled && !!id,
    staleTime: 60 * 1000, // 1 minute for individual diagrams
  });
}

/**
 * Mutation hook for creating a diagram
 */
export function useCreateDiagramMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDiagramData) => createDiagram(data),
    onSuccess: () => {
      // Invalidate diagrams list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.all });
      // Also invalidate subscription info (diagram count changed)
      queryClient.invalidateQueries({ queryKey: queryKeys.subscription.info() });
    },
  });
}

/**
 * Mutation hook for updating a diagram
 */
export function useUpdateDiagramMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDiagramData }) =>
      updateDiagram(id, data),
    onSuccess: (updatedDiagram) => {
      // Update the specific diagram in cache
      queryClient.setQueryData(
        queryKeys.diagrams.detail(updatedDiagram.id),
        updatedDiagram
      );
      // Invalidate list to update metadata
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.list() });
    },
  });
}

/**
 * Mutation hook for patching a diagram (partial update)
 */
export function usePatchDiagramMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDiagramData }) =>
      patchDiagram(id, data),
    onSuccess: (updatedDiagram) => {
      queryClient.setQueryData(
        queryKeys.diagrams.detail(updatedDiagram.id),
        updatedDiagram
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.list() });
    },
  });
}

/**
 * Mutation hook for deleting a diagram
 */
export function useDeleteDiagramMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteDiagram(id),
    onSuccess: (_data, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.diagrams.detail(id) });
      // Invalidate list
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.list() });
      // Update subscription info (diagram count changed)
      queryClient.invalidateQueries({ queryKey: queryKeys.subscription.info() });
    },
  });
}

/**
 * Mutation hook for duplicating a diagram (client-side get + create).
 * Counts against the tier cap, so it can reject with a 403 at the limit.
 */
export function useDuplicateDiagramMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) => duplicateDiagram(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.list() });
      // Diagram count changed → refresh subscription usage
      queryClient.invalidateQueries({ queryKey: queryKeys.subscription.info() });
    },
  });
}

/**
 * Mutation hook for moving a diagram to a folder
 */
export function useMoveDiagramMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ diagramId, folderId }: { diagramId: string; folderId: string | null }) =>
      moveDiagramToFolder(diagramId, folderId),
    onSuccess: (updatedDiagram) => {
      queryClient.setQueryData(
        queryKeys.diagrams.detail(updatedDiagram.id),
        updatedDiagram
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.list() });
    },
  });
}

/**
 * Mutation hook for toggling diagram sharing
 */
export function useToggleSharingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isPublic }: { id: string; isPublic: boolean }) =>
      toggleDiagramSharing(id, isPublic),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.list() });
    },
  });
}

/**
 * Mutation hook for regenerating share token
 */
export function useRegenerateShareTokenMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => regenerateShareToken(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.list() });
    },
  });
}

/**
 * Mutation hook for toggling diagram star status
 */
export function useToggleStarMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isStarred }: { id: string; isStarred: boolean }) =>
      toggleDiagramStar(id, isStarred),
    onMutate: async ({ id, isStarred }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.diagrams.list() });

      // Snapshot the previous value
      const previousDiagrams = queryClient.getQueryData<DiagramMetadata[]>(queryKeys.diagrams.list());

      // Optimistically update
      queryClient.setQueryData<DiagramMetadata[]>(
        queryKeys.diagrams.list(),
        (old) => old?.map((d) => (d.id === id ? { ...d, isStarred } : d))
      );

      return { previousDiagrams };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousDiagrams) {
        queryClient.setQueryData(queryKeys.diagrams.list(), context.previousDiagrams);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.list() });
    },
  });
}

/**
 * Mutation for updating a diagram's icon + color (optimistic on the list).
 */
export function useUpdateDiagramIconMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, icon, iconColor }: { id: string; icon: string | null; iconColor: string | null }) =>
      updateDiagramIcon(id, icon, iconColor),
    onMutate: async ({ id, icon, iconColor }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.diagrams.list() });
      const previousDiagrams = queryClient.getQueryData<DiagramMetadata[]>(queryKeys.diagrams.list());
      queryClient.setQueryData<DiagramMetadata[]>(
        queryKeys.diagrams.list(),
        (old) => old?.map((d) => (d.id === id ? { ...d, icon, iconColor } : d))
      );
      return { previousDiagrams };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousDiagrams) {
        queryClient.setQueryData(queryKeys.diagrams.list(), context.previousDiagrams);
      }
    },
    onSettled: (_data, _err, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.detail(id) });
    },
  });
}

/**
 * Helper to optimistically update diagrams list
 */
export function useOptimisticDiagramUpdate() {
  const queryClient = useQueryClient();

  return {
    updateDiagramInList: (id: string, updates: Partial<DiagramMetadata>) => {
      queryClient.setQueryData<DiagramMetadata[]>(
        queryKeys.diagrams.list(),
        (old) => old?.map((d) => (d.id === id ? { ...d, ...updates } : d))
      );
    },
    removeDiagramFromList: (id: string) => {
      queryClient.setQueryData<DiagramMetadata[]>(
        queryKeys.diagrams.list(),
        (old) => old?.filter((d) => d.id !== id)
      );
    },
  };
}
